import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { productAdminService } from '../../services/api';

const DEFAULT_FILTER = { period: 'month', from: '', to: '' };

// Organizations (and optionally usage totals) for a period; shared by the product-admin list pages
export default function useProductAdminReports({ period = 'month', withUsage = false } = {}) {
  const navigate = useNavigate();
  const [filters, setFilters] = useState({ ...DEFAULT_FILTER, period });
  const [companies, setCompanies] = useState([]);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Expired or missing admin session: back to the login page
  const handleAuthError = useCallback((err) => {
    if ([401, 403].includes(err.response?.status)) {
      localStorage.removeItem('productAdminToken');
      navigate('/product-admin/login', { replace: true });
      return true;
    }
    return false;
  }, [navigate]);

  const load = async () => {
    setLoading(true); setError('');
    try {
      const query = filters.period === 'custom'
        ? { period: 'custom', from: filters.from, to: filters.to }
        : { period: filters.period };
      const [companyResponse, usageResponse] = await Promise.all([
        productAdminService.companies(query),
        withUsage ? productAdminService.usage(query) : Promise.resolve(null),
      ]);
      setCompanies(companyResponse.data.data.companies || []);
      if (usageResponse) setUsage(usageResponse.data.data || null);
    } catch (err) {
      if (!handleAuthError(err)) setError(err.response?.data?.error || 'Unable to load product-admin reports');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return { filters, setFilters, companies, usage, loading, error, load, handleAuthError };
}

// Newest registered organizations first (an organization is as old as its first admin)
export function sortRecentFirst(companies) {
  const registered = (item) => {
    const times = (item.admins || []).map((admin) => Date.parse(admin.created_at)).filter(Number.isFinite);
    return times.length ? Math.min(...times) : 0;
  };
  const firstId = (item) => item.admins?.[0]?.id || 0;
  return [...companies].sort((a, b) => (registered(b) - registered(a)) || (firstId(b) - firstId(a)));
}

export function filterByName(companies, search) {
  const query = search.trim().toLowerCase();
  return companies.filter((item) => !query || (item.company.company_name || '').toLowerCase().includes(query));
}
